import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StatusBadge } from '@/components/StatusBadge'
import { useToast } from '@/hooks/use-toast'
import { lookupByEmail } from '@/services/api'

const schema = z.object({
  email: z.string().email('Veuillez entrer une adresse email valide'),
})

type FormData = z.infer<typeof schema>

interface RequestResult {
  id: string
  client_name: string
  status: string
  created_at: string
}

export default function Lookup() {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<RequestResult[] | null>(null)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setIsLoading(true)
    setResults(null)
    try {
      const response = await lookupByEmail(data.email)
      setResults(response)
      if (response.length === 0) {
        toast({
          title: 'Aucune demande trouvée',
          description: 'Aucune demande d\'onboarding trouvée pour cette adresse email.',
        })
      }
    } catch {
      toast({
        title: 'Erreur',
        description: 'Échec de la recherche. Veuillez réessayer.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Search Form & Results */}
      <div className="w-full lg:w-1/2 bg-white py-8 px-6 lg:px-12 pt-24 overflow-y-auto">
        <div className="max-w-xl mx-auto">
          {/* Back link */}
          <Link to="/" className="text-gold hover:text-gold/80 text-sm font-medium mb-6 inline-block">
            &larr; Retour à l'accueil
          </Link>

          <h1 className="text-2xl lg:text-3xl font-bold text-bronze mb-2">
            Rechercher ma Demande
          </h1>
          <p className="text-muted-foreground mb-8">
            Entrez l'adresse email utilisée lors de la soumission de votre demande d'onboarding.
          </p>

          {/* Search Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mb-8">
            <div>
              <Label htmlFor="email" className="text-foreground">Adresse Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.com"
                className="mt-1 border-input focus:ring-gold focus:border-gold"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-gold hover:bg-gold/90 text-white font-semibold rounded-full py-6"
              disabled={isLoading}
            >
              {isLoading ? 'Recherche en cours...' : 'Rechercher mes Demandes'}
            </Button>
          </form>

          {/* Results */}
          {results !== null && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary/50 px-6 py-4 border-b border-border">
                <h2 className="text-lg font-semibold text-bronze">
                  {results.length} Demande{results.length !== 1 ? 's' : ''} Trouvée{results.length !== 1 ? 's' : ''}
                </h2>
              </div>

              {results.length > 0 ? (
                <div className="divide-y divide-border">
                  {results.map((request) => (
                    <div key={request.id} className="p-4 bg-white hover:bg-secondary/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-foreground">{request.client_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(request.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusBadge status={request.status} />
                          <Link to={`/status/${request.id}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-bronze text-bronze hover:bg-bronze hover:text-white"
                            >
                              Détails
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-muted-foreground">
                  Aucune demande d'onboarding trouvée pour cette adresse email.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-pattern-curves items-start justify-center p-12 pt-24">
        <div className="relative z-10 text-white max-w-md">
          <h2 className="text-3xl font-bold mb-6">
            Retrouvez Vos Demandes
          </h2>
          <p className="text-white/80 mb-8">
            Utilisez l'adresse email de contact pour retrouver le statut de vos demandes d'onboarding.
          </p>

          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <h3 className="font-semibold mb-2">Suivi en Temps Réel</h3>
              <p className="text-white/70 text-sm">
                Consultez l'état d'avancement de votre demande à tout moment.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <h3 className="font-semibold mb-2">Notifications par Email</h3>
              <p className="text-white/70 text-sm">
                Vous recevrez des notifications à chaque changement de statut.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <h3 className="font-semibold mb-2">Historique Complet</h3>
              <p className="text-white/70 text-sm">
                Accédez à l'historique de toutes vos demandes d'onboarding.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
